import * as _ from 'lodash'
import serviceGateway from './serviceGateway';
import store, { getCollection, ModelCollection } from './store';
import util from './util';
import * as globalActions from './actions';
import employeeFactory from './models/employeeModel';
import socketClient from './socketClient';

const _lastFetch: { employees?: number } = {};
const _employeeExpirationMs = 60 * 60 * 8 * 1000; // 8 hours - socket client should keep us fresh enough otherwise

export const socketClientHandlers = {
	['model.employee.added']: message => {
		fetchEmployee({ key: message.payload.key });
	},
	['model.employee.updated']: message => {
		const keyPath = ['model', 'employees', message.payload.key];
		const existing = store().getIn(keyPath);

		if (existing) {
			if (existing.itemVersion === message.payload.patchTargetVersion) {
				existing.applyPatch(message.payload.patch);
				store().setIn(keyPath, existing);
			}
			else if (existing.itemVersion < message.payload.patchTargetVersion) {
				fetchEmployee({ key: message.payload.key });
			}
		}
		else {
			fetchEmployee({ key: message.payload.key });
		}
	},
	['model.employee.removed']: message => {
		const keyPath = ['model', 'employees', message.payload.key];

		store().deleteIn(keyPath);
	}
};

// wrap handlers to allow extensions to override
for (const key of _.keys(socketClientHandlers)) {
	socketClient.on(key, message => {
		socketClientHandlers[key](message);
	});
}

function setEmployee (keyPath, employeeModel, isSelf) {
	store().withMutations(s => {
		const snapshot = (!employeeModel) ? s.deleteIn(keyPath) : s.setIn(keyPath, employeeModel);

		if (isSelf) {
			snapshot.setIn(['viewState', 'currentEmployee'], employeeModel).setIn(['viewState', 'currentUser'], employeeModel.user);
		}
	});
}

export const fetchEmployees = () => {
	if (_lastFetch.employees && (Date.now() - _lastFetch.employees < _employeeExpirationMs)) {
		return Promise.resolve();
	}
	return serviceGateway.fetchEmployees()
		.then(result => {
			const collection = getCollection({ collection: ModelCollection.employees });

			collection.withMutations(c => {
				_.forEach(result.employees, e => {
					c.set(e.key, employeeFactory(e));
				});
			});

			_lastFetch.employees = Date.now();
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

// NOTE: params.key will return employee by key, params.userKey will return employee by user key
export const fetchEmployee = params => {
	return serviceGateway.fetchEmployee(params)
		.then(result => {
			if (result.employee) {
				const collection = getCollection({ collection: ModelCollection.employees });

				collection.set(result.employee.key, employeeFactory(result.employee));
			}
		})
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const sendInvitation = params => {
	return serviceGateway.sendInvitation(params)
		.catch(err => {
			globalActions.pushAlert({ type: 'warning', message: err.message || err, isCloseable: true });
		});
};

export const setProfileValue = params => {
	const targetEmployee = params.employee || store().getIn(['viewState', 'currentEmployee']);
	const mutatedEmployee = employeeFactory(targetEmployee.toDocument());

	mutatedEmployee.setProfileValue(params.name, params.value);

	return saveEmployee({ employee: mutatedEmployee, patch: mutatedEmployee.getPatch(targetEmployee) });
};

export const saveEmployee = params => {
	const keyPath = ['model', 'employees', params.employee.key];
	const currentEmployee = store().getIn(['viewState', 'currentEmployee']);
	const isSelf = (currentEmployee && currentEmployee.key === params.employee.key);
	let avatarBlob;

	if (params.employee.avatarUrl && params.employee.avatarUrl.indexOf('data:') === 0) {
		avatarBlob = util.dataURItoBlob(params.employee.avatarUrl);
		params.patch = _.filter(params.patch, op => op.path !== '/avatarUrl');
	}

	setEmployee(keyPath, employeeFactory(params.employee), isSelf);

	if (avatarBlob) { params.employee = _.omit(params.employee, 'avatarUrl'); }

	return serviceGateway.saveEmployee({ employee: params.employee, patch: params.employee.dateCreated ? params.patch : undefined })
		.then(result => {
			if (avatarBlob && result.uploadUrl) {
				const uploadUrl = result.uploadUrl;

				return serviceGateway.uploadFile({ url: uploadUrl, file: avatarBlob })
					.then(() => {
						// add a guid to break the browser cache
						result.employee.avatarUrl = uploadUrl.split('?')[0] + '?' + util.generateGuid();
						return serviceGateway.saveEmployee({ employee: result.employee, patch: [{ op: 'replace', path: '/avatarUrl', value: result.employee.avatarUrl }]});
					})
					.then(avatarResult => {
						setEmployee(keyPath, employeeFactory(avatarResult.employee), isSelf);
					})
					.catch(() => {
						setEmployee(keyPath, employeeFactory(result.employee), isSelf);
					});
			}
			else {
				setEmployee(keyPath, employeeFactory(result.employee), isSelf);
			}
		})
		.catch(err => {
			const key = util.generateGuid();
			const retry = p => {
				globalActions.clearAlert({ key: key });
				return saveEmployee(p || params);
			};

			globalActions.pushAlert({
				key: key,
				type: 'error',
				message: err.message || 'Server Error',
				source: 'server',
				action: 'saveEmployee',
				retry: retry,
				params: params,
				orgininalError: err,
				isCloseable: true
			});
		});
};

export const deleteEmployee = params => {
	const keyPath = ['model', 'employees', params.key];
	const preDeleteModel = store().getIn(keyPath);

	store().deleteIn(keyPath);
	serviceGateway.deleteEmployee({ employee: preDeleteModel })
		.catch(err => {
			store().setIn(keyPath, preDeleteModel);
			const key = util.generateGuid();
			const retry = p => {
				globalActions.clearAlert({ key: key });
				return deleteEmployee(p || params);
			};

			globalActions.pushAlert({
				key: key,
				type: 'error',
				message: err.message || 'Server Error',
				source: 'server',
				action: 'deleteEmployee',
				retry: retry,
				params: params,
				orgininalError: err,
				isCloseable: true
			});
		});
};

export const getEmployeeByUserKey = userKey => {
	return getCollection({ collection: ModelCollection.employees })
		.find(f => {
			return f.user.key === userKey;
		});
};